// First-party error reporter — consent-gated, DPDP-clean.
// - Reads diagnostics consent from localStorage (`ap_cookie_consent_v1`).
// - Buffers events until consent is granted; drops if user opts out.
// - Sends to /api/errors (no third parties).
// - Redacts obvious tokens client-side; server does defence-in-depth redaction.

import api from "@/lib/api";

const CONSENT_KEY = "ap_cookie_consent_v1";
const BUFFER_MAX = 20;
let buffer = [];
let installed = false;

function hasDiagnosticsConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed?.diagnostics;
  } catch {
    return false;
  }
}

function redactClient(text) {
  if (!text) return text;
  return String(text)
    .slice(0, 4000)
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer [token]")
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "[email]");
}

async function ship(event) {
  try {
    await api.post("/errors", event);
  } catch {
    // Never let the reporter itself throw.
  }
}

export function reportError({ message, stack, source = "manual", level = "error", meta }) {
  const event = {
    message: redactClient(message || "unknown"),
    stack: redactClient(stack),
    level,
    source,
    route_path: typeof window !== "undefined" ? window.location.pathname : undefined,
    meta,
  };
  if (hasDiagnosticsConsent()) {
    // Flush any buffered events first.
    if (buffer.length) {
      const drained = buffer;
      buffer = [];
      drained.forEach(ship);
    }
    ship(event);
  } else if (buffer.length < BUFFER_MAX) {
    buffer.push(event);
  }
}

export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => {
    reportError({
      message: e?.message || "window.onerror",
      stack: e?.error?.stack,
      source: "window",
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    reportError({
      message: reason?.message || String(reason || "unhandledrejection"),
      stack: reason?.stack,
      source: "promise",
    });
  });
}

// Called by CookieBanner after user grants/updates consent so we can flush.
export function onDiagnosticsConsentChanged() {
  if (!hasDiagnosticsConsent() || !buffer.length) return;
  const drained = buffer;
  buffer = [];
  drained.forEach(ship);
}
